import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  Injectable,
} from '@angular/core';
import {
  ToastContainer,
  ToastData,
  ToastPosition,
  ToastType,
} from '../components/toast';

type ToastPositions =
  | 'toast-top-left'
  | 'toast-top-right'
  | 'toast-bottom-left'
  | 'toast-bottom-right'
  | 'toast-bottom-center';

type ToastColors = 'success' | 'info' | 'warning' | 'error';

export type ToastProps = {
  title?: string;
  message: string;
  type: ToastColors;
  positionClass?: ToastPositions;
};

@Injectable({ providedIn: 'root' })
export class ToastService {
  private containers = new Map<
    ToastPosition,
    { ref: ComponentRef<ToastContainer>; element: HTMLElement }
  >();
  private toastIdCounter = 0;

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector
  ) {}

  private getOrCreateContainer(position: ToastPosition) {
    if (!this.containers.has(position)) {
      const containerElement = document.createElement('div');
      document.body.appendChild(containerElement);

      const componentRef = createComponent(ToastContainer, {
        environmentInjector: this.injector,
        hostElement: containerElement,
      });

      (componentRef.instance as ToastContainer).position = position;
      this.appRef.attachView(componentRef.hostView);

      const hostElement = document.createElement('div');
      containerElement
        .querySelector('.toast-container')
        ?.appendChild(hostElement);

      this.containers.set(position, {
        ref: componentRef,
        element: hostElement,
      });
    }

    return this.containers.get(position)!;
  }

  private createToast(
    message: string,
    title: string | undefined,
    type: ToastType,
    positionClass: ToastPosition
  ) {
    const container = this.getOrCreateContainer(positionClass);

    const toastData: ToastData = {
      id: `toast-${++this.toastIdCounter}`,
      title,
      message,
      type,
      timeOut: 5000,
      closeButton: true,
      progressBar: true,
      enableHtml: false,
      titleClass: 'toast-title',
      messageClass: 'toast-message',
    };

    (container.ref.instance as ToastContainer).addToast(
      toastData,
      container.element
    );
  }

  show(props: ToastProps): void {
    const text = props?.message ?? '';
    const title = props?.title ?? '';
    const type = props?.type ?? 'info';
    const positionClass = props?.positionClass ?? 'toast-top-right';

    this.createToast(text, title, type, positionClass);
  }

  success(message: string): void;
  success(props: Omit<ToastProps, 'type'>): void;

  success(props: string | Omit<ToastProps, 'type'>): void {
    if (typeof props === 'string') {
      this.createToast(
        props ?? '',
        undefined,
        'success',
        'toast-bottom-center'
      );
      return;
    }

    const text = props?.message ?? '';
    const title = props?.title ?? '';
    const positionClass = props?.positionClass ?? 'toast-top-right';

    this.createToast(text, title, 'success', positionClass);
  }

  error(message: string): void;
  error(props: Omit<ToastProps, 'type'>): void;

  error(props: string | Omit<ToastProps, 'type'>): void {
    if (typeof props === 'string') {
      this.createToast(props ?? '', undefined, 'error', 'toast-bottom-center');
      return;
    }

    const text = props?.message ?? '';
    const title = props?.title ?? '';
    const positionClass = props?.positionClass ?? 'toast-top-right';

    this.createToast(text, title, 'error', positionClass);
  }

  info(message: string): void;
  info(props: Omit<ToastProps, 'type'>): void;

  info(props: string | Omit<ToastProps, 'type'>): void {
    if (typeof props === 'string') {
      this.createToast(props ?? '', undefined, 'info', 'toast-bottom-center');
      return;
    }

    const text = props?.message ?? '';
    const title = props?.title ?? '';
    const positionClass = props?.positionClass ?? 'toast-top-right';

    this.createToast(text, title, 'info', positionClass);
  }

  warning(message: string): void;
  warning(props: Omit<ToastProps, 'type'>): void;

  warning(props: string | Omit<ToastProps, 'type'>): void {
    if (typeof props === 'string') {
      this.createToast(
        props ?? '',
        undefined,
        'warning',
        'toast-bottom-center'
      );
      return;
    }

    const text = props?.message ?? '';
    const title = props?.title ?? '';
    const positionClass = props?.positionClass ?? 'toast-top-right';

    this.createToast(text, title, 'warning', positionClass);
  }
}
